import type { HeaderLinkAction, HeaderNavItem, HeaderPlacement } from './model';

/**
 * The public site's primary navigation, in display order. Adapters turn each path into an
 * environment URL; `owner` names the app that serves it so an adapter can pick the right host.
 */
export const SITE_NAVIGATION = [
  { id: 'sell', label: 'Sell', path: '/cash-now-more-later', owner: 'beacon' },
  { id: 'buy', label: 'Buy', path: '/homes', owner: 'cosmos' },
  { id: 'agents', label: 'Agents', path: '/agents', owner: 'beacon' },
  { id: 'mortgage', label: 'Mortgage', path: '/mortgage', owner: 'beacon' },
] as const;

export type SiteNavigationDestination = (typeof SITE_NAVIGATION)[number];

type SiteNavigationOptions = {
  /** The app rendering the header. Its own routes use its router; the rest load a new document. */
  app: SiteNavigationDestination['owner'];
  /** Turns a destination path into a URL on the host that serves it. */
  hrefFor: (destination: SiteNavigationDestination) => HeaderLinkAction['href'];
  isCurrent?: (destination: SiteNavigationDestination) => boolean;
  onSelect?: (destination: SiteNavigationDestination, placement: HeaderPlacement) => void;
};

export function siteNavigationItems({
  app,
  hrefFor,
  isCurrent,
  onSelect,
}: SiteNavigationOptions): HeaderNavItem[] {
  return SITE_NAVIGATION.map((destination) => ({
    kind: 'link',
    key: destination.id,
    label: destination.label,
    current: isCurrent?.(destination) ?? false,
    action: {
      kind: 'link',
      href: hrefFor(destination),
      navigation: destination.owner === app ? 'app' : 'document',
      ...(onSelect
        ? { onSelect: (placement: HeaderPlacement) => onSelect(destination, placement) }
        : {}),
    },
  }));
}

export const HELP_CENTER_URL = 'https://help.opendoor.com/';

/** Account destinations by path. Adapters choose the host and the navigation mode. */
export const ACCOUNT_PATHS = {
  signIn: '/login',
  dashboard: '/dashboard',
  account: '/account',
  properties: '/properties',
  closingDashboard: '/closing-dashboard',
} as const;
